import api from './api';

export type SubscriptionTier = 'free' | 'basic' | 'plus';

export interface RoastQuotaSnapshot {
  tier: SubscriptionTier;
  limit_kg: number;
  used_kg: number;
  remaining_kg: number;
  cycle_start: string; // ISO
  cycle_end: string;   // ISO
}

export interface MachineTypeQuotaSnapshot {
  tier: SubscriptionTier;
  /** -1 means unlimited (plus tier). */
  limit: number;
  /** Total rows (active + inactive). Gates creation. */
  used: number;
  /** is_active=true rows. Gates activation. */
  active: number;
  /** -1 means unlimited; otherwise limit - used. */
  remaining: number;
}

export interface SubscriptionState {
  tier: SubscriptionTier;
  expires_at: string | null;
  started_at: string;
  days_remaining: number | null;
  roast_quota?: RoastQuotaSnapshot;
  machine_type_quota?: MachineTypeQuotaSnapshot | null;
}

export interface VoucherRedemptionRow {
  id: string;
  voucher_id: string;
  company_id: string;
  redeemed_by: string;
  redeemed_at: string;
  months_added: number;
  subscription_extended_to: string;
  // Populated by the super_admin audit endpoint via JOIN; omitted on the
  // per-company history endpoint.
  company_name?: string;
}

export interface RedeemResult {
  tier: 'basic';
  expires_at: string;
  voucher_name: string;
  months_added: number;
}

export interface VoucherRow {
  id: string;
  code: string;
  name: string;
  duration_months: number;
  expires_at: string;
  max_redemptions: number | null;
  redemption_count: number;
  status: 'active' | 'expired' | 'exhausted' | 'deleted';
  created_at: string;
}

export interface VoucherDetailResponse {
  voucher: VoucherRow;
  status: VoucherRow['status'];
  redemptions: VoucherRedemptionRow[];
}

export interface CreateVoucherInput {
  name: string;
  code?: string;
  duration_months: number;
  expires_at: string; // ISO
  max_redemptions?: number | null;
}

export interface UpdateVoucherInput {
  name?: string;
  expires_at?: string;
  max_redemptions?: number | null;
  clear_max_redemptions?: boolean;
}

export interface CompanySubscriptionRow {
  company_id: string;
  company_name: string;
  tier: SubscriptionTier;
  expires_at: string | null;
  started_at: string;
  updated_at: string;
}

export interface AdjustInput {
  months_delta: number;
  tier?: SubscriptionTier;
  reason: string;
}

export interface AdjustmentRow {
  id: string;
  company_id: string;
  adjusted_by: string;
  adjusted_at: string;
  months_delta: number;
  previous_tier: string;
  new_tier: string;
  previous_expires_at: string | null;
  new_expires_at: string | null;
  reason: string;
}

export const subscriptionService = {
  // Per-tenant
  getMine: () => api.get<SubscriptionState>('/subscription').then(r => r.data),
  redeem: (code: string) =>
    api.post<RedeemResult>('/subscription/redeem', { code }).then(r => r.data),
  listMyRedemptions: () =>
    api.get<VoucherRedemptionRow[]>('/subscription/redemptions').then(r => r.data),

  // Super_admin — vouchers
  adminListVouchers: () => api.get<VoucherRow[]>('/admin/vouchers').then(r => r.data),
  adminCreateVoucher: (input: CreateVoucherInput) =>
    api.post<VoucherRow>('/admin/vouchers', input).then(r => r.data),
  adminVoucherDetail: (id: string) =>
    api.get<VoucherDetailResponse>(`/admin/vouchers/${id}`).then(r => r.data),
  adminUpdateVoucher: (id: string, input: UpdateVoucherInput) =>
    api.patch<void>(`/admin/vouchers/${id}`, input),
  adminDeleteVoucher: (id: string) => api.delete<void>(`/admin/vouchers/${id}`),

  // Super_admin — companies
  adminListCompanies: () =>
    api.get<CompanySubscriptionRow[]>('/admin/companies/subscriptions').then(r => r.data),
  adminAdjust: (companyId: string, input: AdjustInput) =>
    api.post<AdjustmentRow>(`/admin/companies/${companyId}/subscription/adjust`, input).then(r => r.data),
  adminListAdjustments: (companyId: string) =>
    api.get<AdjustmentRow[]>(`/admin/companies/${companyId}/subscription/adjustments`).then(r => r.data),
};
